import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerStyles from './styles/outline-locations-map.inner.pcss?inline';
import globalStyles from './styles/outline-locations-map.global.pcss?inline';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import mapStyles from './styles/map-styles.json';
import { Location, JSONDay, Coords } from '../../utilities/types';
import locationTestData from './location-test-data.json';
import { formatPhoneNumberForDisplay, formatPhoneNumberForHref } from '../../utilities/phone-number-formatter';

// Define interfaces for marker types that we can't easily get from Google Maps
interface MarkerInterface {
  position: google.maps.LatLng | google.maps.LatLngLiteral;
  map?: google.maps.Map;
  content?: HTMLElement;
  addListener(event: string, handler: Function): google.maps.MapsEventListener;
}

/**
 * The OutlineLocationsMap component renders a Google Map displaying hospital and service locations
 * with interactive pins, info cards, and directions. Features include:
 * - Clustered location markers with dynamic scaling.
 * - Interactive info cards with location details.
 * - Support for custom location images and hours.
 * - Responsive design with mobile optimization.
 * - Real-time distance and drive time calculations.
 *   - Distance is calculated using the Haversine formula.
 *   - Drive time is calculated when a marker is clicked using the Google Maps Directions API.
 *
 * @element outline-locations-map
 *
 * @property {string} data-url - API endpoint for fetching location data
 * @property {string} data-json - JSON data for locations (alternative to data-url)
 *
 * @example
 * ```html
 * <outline-locations-map
 *   data-url="https://example.com/api/locations">
 * </outline-locations-map>
 * ```
 *
 * @requires Google Maps API key must be configured in the environment variables
 * (VITE_DEVELOPMENT_GOOGLE_MAP_API_KEY) for the map to function properly.
 */
@customElement('outline-locations-map')
export class OutlineLocationsMap extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  /**
   * API endpoint for
   */
  @property({ type: String, attribute: 'data-url', reflect: true })
  dataURL: string;

  /**
   * JSON data for locations
   */
  @property({ type: String, attribute: 'data-json' })
  dataJSON: string;

  /**
   * Size variation for map
   */
  @property({ type: String, attribute: 'size' })
  size: 'small' | 'medium' | 'large' = 'large';

  /**
   * Google maps API loader
   */
  private _loader: Loader;

  /**
   * Hospital/Service location data
   */
  locationsData: Array<Location> = [];

  /**
   * User geolocation
   */
  private _currentLocation: Coords;

  /**
   * DirectionsService instance (created only when needed)
   */
  private _directionsService: google.maps.DirectionsService | null = null;

  /**
   * Map reference for use in multiple methods
   */
  private _map: google.maps.Map | null = null;

  /**
   * InfoWindow reference for use in multiple methods
   */
  private _infoWindow: google.maps.InfoWindow | null = null;

  /**
   * Mapping of position keys to location arrays
   */
  private _locationsByPosition: Map<string, Location[]> = new Map();

  /**
   * Mapping of markers to locations
   */
  private _markerToLocationMap: Map<MarkerInterface, Location> = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.getLocationInfo();
    this.ensureGoogleMapsLoaded().then(() => {
      if (this.dataJSON) {
        this.processJSONData(this.dataJSON);
      } else {
        this.setLocationData(this.dataURL);
      }
    });
  }

  /**
   * Process JSON data passed in via data-json attribute
   */
  processJSONData(jsonString: string) {
    try {
      const locationsData = JSON.parse(jsonString);
      if (locationsData && Array.isArray(locationsData)) {
        // Transform provider office locations into Location objects
        this.locationsData = locationsData.map(
          (office: {
            name?: string;
            address?: {
              line1?: string;
              line2?: string;
              city?: string;
              region?: string;
              postalCode?: string;
            };
            mainPhone?: string;
            fax?: string;
            yextDisplayCoordinate?: {
              latitude: number;
              longitude: number;
            };
            c_practiceName?: string;
            c_locationPageURL?: string;
          }) => {
            const location: Location = {
              location_name: office.c_practiceName || '',
              location_type: 'clinic_location',
              location_address: this.formatOfficeAddress(office.address),
              phone: office.mainPhone || '',
              fax: office.fax || '',
              // Initialize all required fields
              location_url: office.c_locationPageURL || '',
              cta_url: '',
              location_image: '',
              location_hours: '',
              location_lat: office.yextDisplayCoordinate?.latitude || 0,
              location_lon: office.yextDisplayCoordinate?.longitude || 0,
              location_address_line_1: office.address?.line1 || '',
              location_address_line_2: office.address?.line2 || '',
              location_city_state_zip:
                (office.address?.city || '') +
                ', ' +
                (office.address?.region || '') +
                ' ' +
                (office.address?.postalCode || ''),
              hours_markup: '',
            };

            return location;
          }
        );

        this.initializeMap();
      } else {
        console.error('Invalid JSON data format: missing locations array');
      }
    } catch (error) {
      console.error('Error processing JSON data:', error);
    }
  }

  /**
   * Format address from provider data format to location format
   */
  formatOfficeAddress(address?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
  }): string {
    if (!address) return '';

    const line1 = address.line1 || '';
    const line2 = address.line2 || '';
    const city = address.city || '';
    const region = address.region || '';
    const postalCode = address.postalCode || '';

    let formattedAddress = line1;
    if (line2) formattedAddress += '\n' + line2;
    formattedAddress += '\n' + city + ', ' + region + ' ' + postalCode;

    return formattedAddress;
  }

  /**
   * Initializes map instance, calls all processing methods.
   */
  async initializeMap() {
    const mapTarget = this.shadowRoot?.querySelector('#map') as HTMLElement;
    const { Map, InfoWindow } = (await google.maps.importLibrary(
      'maps'
    )) as google.maps.MapsLibrary;
    // Use type assertion for the marker library
    const markerLibrary = (await google.maps.importLibrary('marker')) as {
      AdvancedMarkerElement: new (options: {
        map?: google.maps.Map;
        position?: google.maps.LatLngLiteral;
        content?: HTMLElement;
      }) => MarkerInterface;
    };
    const AdvancedMarkerElement = markerLibrary.AdvancedMarkerElement;

    this._map = new Map(mapTarget, {
      mapId: 'bjc_map',
      fullscreenControl: false,
      mapTypeControlOptions: { mapTypeIds: ['styled_map', 'satellite'] },
      maxZoom: 17,
      zoom: 17,
    });

    const map = this._map;
    map.mapTypes.set(
      'styled_map',
      new google.maps.StyledMapType(mapStyles, { name: 'Styled Map' })
    );
    map.setMapTypeId('styled_map');

    const geocoder = new google.maps.Geocoder();
    this._infoWindow = new InfoWindow({ disableAutoPan: false });
    const infoWindow = this._infoWindow;

    // Geocode all locations before rendering markers
    this.locationsData = await this.geocodeLocations(
      this.locationsData,
      geocoder
    );

    // Group locations by position for handling same-coordinate cases
    this._groupLocationsByPosition();

    // Set map boundaries
    const bounds = new google.maps.LatLngBounds();
    if (this._currentLocation) {
      bounds.extend(this._currentLocation);
    }

    // set pin styles
    const markers = this.locationsData
      .map(location => {
        const customPin = document.createElement('div');
        customPin.classList.add(
          location.location_type === 'hospital_location'
            ? 'hospital-pin'
            : 'clinic-pin'
        );
        if (!location.location_lat || !location.location_lon) {
          console.log(
            'missing lat or long for',
            location.location_name,
            ' marker creation'
          );
          return undefined;
        } else {
          // Calculate straight-line distance for initial display
          if (this._currentLocation) {
            location.distance = this.calculateStraightLineDistance(
              this._currentLocation.lat,
              this._currentLocation.lng,
              location.location_lat,
              location.location_lon
            );
          }

          // Create position key for grouping
          const posKey = `${location.location_lat},${location.location_lon}`;
          const locationsAtPosition =
            this._locationsByPosition.get(posKey) || [];

          // instantiate marker
          const marker = new AdvancedMarkerElement({
            map,
            position: {
              lat: location.location_lat,
              lng: location.location_lon,
            },
            content: customPin,
          });

          // Store reference to location
          this._markerToLocationMap.set(marker, location);

          // Add card generating click event
          marker.addListener('click', () => {
            // If multiple locations at this position, show the multi-choice infowindow
            if (locationsAtPosition.length > 1) {
              this.showMultiLocationOptions(locationsAtPosition, marker);
            } else {
              // Get drive time only when a marker is clicked
              this.getDirectionsForLocation(location).then(() => {
                infoWindow.setContent(
                  this.generateLocationCard(location, infoWindow)
                );
                // clear any existing active
                this.shadowRoot
                  ?.querySelector('[active-pin]')
                  ?.removeAttribute('active-pin');
                infoWindow.open(map, marker);
                if (marker.content) {
                  (marker.content as HTMLElement).setAttribute(
                    'active-pin',
                    'active-pin'
                  );
                }
              });
            }
          });

          bounds.extend(
            new google.maps.LatLng(location.location_lat, location.location_lon)
          );
          return marker;
        }
      })
      .filter(marker => marker !== undefined);

    /**
     * Handle clustered markers.
     */
    const markerClusterer = new MarkerClusterer({
      markers: markers.filter(Boolean) as unknown as google.maps.Marker[],
      map: map,
      renderer: {
        render: ({ markers, position }) => {
          const svg = window.btoa(`
        <svg fill="#00449D" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
          <circle cx="120" cy="120" opacity="0.9" r="70" />
        </svg>`);

          // Handle the case when markers might be undefined
          const markerCount = markers?.length || 0;
          const scale = markerCount < 10 ? 70 : 40;

          const marker = new google.maps.Marker({
            position: {
              lat: position.lat(),
              lng: position.lng(),
            },
            label: {
              text: String(markerCount),
              color: 'white',
            },
            icon: {
              url: `data:image/svg+xml;base64,${svg}`,
              scaledSize: new google.maps.Size(scale, scale),
            },
          });

          // Add click handler to clusters
          google.maps.event.addListener(marker, 'click', () => {
            const zoom = map.getZoom() || 0;
            const maxZoom = 18; // Maximum zoom level for Google Maps

            // Check if we're at max zoom and there are multiple markers
            if (zoom >= maxZoom - 1 && markerCount > 1) {
              // Check if this is a cluster of markers at exactly the same position
              const posKey = `${position.lat()},${position.lng()}`;
              const locationsAtPosition = this._locationsByPosition.get(posKey);

              if (locationsAtPosition && locationsAtPosition.length > 1) {
                // Show multi-choice for markers at same position
                this.showMultiLocationOptions(locationsAtPosition, marker);
                return false;
              }
            }

            // Default cluster behavior - zoom in
            return true;
          });

          return marker;
        },
      },
    });

    window.addEventListener('keydown', e => this.escHandler(e, infoWindow));
    map.fitBounds(bounds);
  }

  /**
   * Group locations by their lat/lng position for easier handling of same-coordinate cases
   */
  private _groupLocationsByPosition() {
    this._locationsByPosition.clear();

    this.locationsData.forEach(location => {
      if (location.location_lat && location.location_lon) {
        const key = `${location.location_lat},${location.location_lon}`;

        if (!this._locationsByPosition.has(key)) {
          this._locationsByPosition.set(key, []);
        }

        this._locationsByPosition.get(key)?.push(location);
      }
    });
  }

  /**
   * Shows an InfoWindow with options when multiple locations share the same coordinates
   */
  showMultiLocationOptions(
    locations: Location[],
    marker: MarkerInterface | google.maps.Marker
  ) {
    if (!this._infoWindow || !this._map) return;

    // Create a container for the location options
    const wrapperDiv = document.createElement('div');
    wrapperDiv.classList.add('multi-location-wrapper');

    // Create button for closing the info window
    const closeButton = document.createElement('button');
    closeButton.setAttribute('tabindex', '0');
    closeButton.classList.add('location-card--close');
    closeButton.addEventListener('click', () =>
      this.infoWindowCloseClickHandler(this._infoWindow!)
    );

    // Create a list of location options using template literals
    const locationOptionsMarkup = () => {
      let listItems = '';
      locations.forEach(location => {
        const locationTypeLabel =
          location.location_type === 'hospital_location'
            ? 'Hospital'
            : 'Clinic';

        listItems += `
          <li>
            <button class="multi-location-option" data-location-id="${location.location_name}">
              ${location.location_name || 'Unnamed Location'}
              ${location.location_type ? `<span class="location-type">${locationTypeLabel}</span>` : ''}
            </button>
          </li>
        `;
      });
      return listItems;
    };

    // Build the complete markup
    const multiLocationMarkup = `
      <div class="multi-location-card">
        <div class="multi-location-header">
          <div class="multi-location-title">Multiple locations at this address</div>
        </div>
        <div class="multi-location-content">
          <ul class="multi-location-list">
            ${locationOptionsMarkup()}
          </ul>
        </div>
      </div>
    `;

    // Set the HTML content
    wrapperDiv.innerHTML = multiLocationMarkup;

    // Add the close button to the header
    wrapperDiv
      .querySelector('.multi-location-header')
      ?.appendChild(closeButton);

    // Add click event handlers to all location buttons
    wrapperDiv
      .querySelectorAll('.multi-location-option')
      .forEach((button, index) => {
        button.addEventListener('click', () => {
          const location = locations[index];
          this.getDirectionsForLocation(location).then(() => {
            this._infoWindow!.setContent(
              this.generateLocationCard(location, this._infoWindow!)
            );
            // clear any existing active
            this.shadowRoot
              ?.querySelector('[active-pin]')
              ?.removeAttribute('active-pin');

            // Check what type of marker we're dealing with and set active pin state
            if ('content' in marker && marker.content) {
              // It's an AdvancedMarkerElement
              (marker.content as HTMLElement).setAttribute(
                'active-pin',
                'active-pin'
              );
            }
          });
        });
      });

    // Set the content and open the InfoWindow
    this._infoWindow.setContent(wrapperDiv);
    this._infoWindow.open(this._map, marker);
  }

  /**
   * Calculate the straight-line distance between two points using the Haversine formula
   * @returns A formatted distance string in miles (e.g., "5.2 mi")
   */
  calculateStraightLineDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): string {
    // Earth's radius in miles
    const radius = 3958.8;

    // Convert latitude and longitude from degrees to radians
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    // Haversine formula
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = radius * c;

    // Format the distance
    return distance < 10
      ? `${distance.toFixed(1)} miles away`
      : `${Math.round(distance)} miles away`;
  }

  /**
   * Get directions and drive time for a specific location (only when marker is clicked)
   */
  async getDirectionsForLocation(location: Location): Promise<void> {
    // Skip if we already have drive time or don't have current location
    if (
      location.drive_time ||
      !this._currentLocation?.lat ||
      !this._currentLocation?.lng
    ) {
      return Promise.resolve();
    }

    // Initialize DirectionsService if it hasn't been created yet
    if (!this._directionsService) {
      this._directionsService = new google.maps.DirectionsService();
    }

    try {
      const response = await this._directionsService.route({
        origin: {
          lat: this._currentLocation.lat,
          lng: this._currentLocation.lng,
        },
        destination: { lat: location.location_lat, lng: location.location_lon },
        travelMode: google.maps.TravelMode.DRIVING,
      });

      location.drive_time = response.routes[0].legs[0].duration?.text;
      // Replace the estimated distance with the actual driving distance
      const distanceText = response.routes[0].legs[0].distance?.text;
      // Check if the distance text ends with "mi" and replace it with "miles away"
      location.distance = distanceText?.endsWith(' mi')
        ? distanceText.replace(' mi', ' miles away')
        : distanceText;
      return Promise.resolve();
    } catch (error) {
      return Promise.resolve();
    }
  }

  /**
   * Close existing windows and active states when Escape is pressed.
   */
  escHandler(e: KeyboardEvent, info: google.maps.InfoWindow) {
    if (e.key === 'Escape') {
      const self = document.querySelector(
        'outline-locations-map'
      ) as OutlineLocationsMap;
      if (self) {
        self.infoWindowCloseClickHandler(info);
        self.shadowRoot
          ?.querySelector('[active-pin]')
          ?.removeAttribute('active-pin');
      }
    }
  }

  /**
   * Takes processed address information and determines map coordinates for pin placement.
   */
  async geocodeLocations(
    locations: Array<Location>,
    geocoder: google.maps.Geocoder
  ): Promise<Location[]> {
    return Promise.all(
      locations.map(async location => {
        // Always process the address first for formatting
        this.processAddress(location);

        if (!location.location_lat || !location.location_lon) {
          // First check if field_geolocation exists and has content
          if (location.field_geolocation && location.field_geolocation.trim()) {
            // Clean up the geolocation string (remove newlines and trim)
            const geoString = location.field_geolocation
              .replace(/\n/g, '')
              .trim();
            // Split by comma and parse as lat,lon
            const [lat, lon] = geoString
              .split(',')
              .map((coord: string) => parseFloat(coord.trim()));

            // Only assign if we have valid numbers
            if (!isNaN(lat) && !isNaN(lon)) {
              location.location_lat = lat;
              location.location_lon = lon;
              return location;
            }
          }

          // If no valid geolocation field or parsing failed, proceed with geocoding
          if (
            location.location_address_line_1 ||
            location.location_city_state_zip
          ) {
            try {
              console.log('get lat/lon from address');
              const response = await geocoder.geocode({
                address: `${location.location_address_line_1} ${location.location_city_state_zip}`,
              });
              if (response.results[0]) {
                const { lat, lng } =
                  response.results[0].geometry.location.toJSON();
                location.location_lat = lat;
                location.location_lon = lng;
              }
            } catch (error) {
              console.error('Geocoding error for:', location, error);
            }
          }
        }
        return location;
      })
    );
  }

  /**
   * Processes Drupal generated address string.
   */
  processAddress(location: Location) {
    if (location.location_address && location.location_address !== '') {
      // Split the address into lines
      const spliced = location.location_address.split('\n');

      // Filter out "United States" line if present
      const filteredLines = spliced.filter(line => line !== 'United States');

      if (filteredLines.length > 1) {
        location.location_address_line_1 = filteredLines[0];

        if (filteredLines.length > 2) {
          // Put second line in location_address_line_2
          location.location_address_line_2 = filteredLines[1];
          // Join remaining lines into city_state_zip
          location.location_city_state_zip = filteredLines.slice(2).join('\n');
        } else if (filteredLines.length == 2) {
          // If only 2 lines, put second line in city_state_zip
          location.location_city_state_zip = filteredLines[1];
        }

        return location;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Process stringified JSON and generates markup string for hours.
   */
  processHours(location: Location) {
    if (location.location_hours && location.location_hours !== undefined) {
      const hoursInfo = JSON.parse(location.location_hours);
      let list = '';
      hoursInfo.map((day: JSONDay) => {
        const dayMarkup = `<li>${day.label ? `<span class="hours-label">${day.label}</span>` : ''}  ${day.text ? `<span class="hours-info">${day.text}</span>` : ''}</li>`;
        list += dayMarkup;
      });
      location.hours_markup = list;
      return location;
    } else {
      return false;
    }
  }

  /**
   * Sets current location if browser allows.
   */
  getLocationInfo() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this._currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        },
        () => {
          console.log('Error retrieving location information.');
        }
      );
    } else {
      console.log('Your browser does not support geolocation.');
    }
  }

  infoWindowCloseClickHandler = (info: google.maps.InfoWindow) => {
    info.close();
    info.setContent(null);
    this.shadowRoot
      ?.querySelector('[active-pin]')
      ?.removeAttribute('active-pin');
  };

  /**
   * Processes address, hours, and direction url. Generates location card markup.
   */
  generateLocationCard(
    location: Location,
    infoWindow: google.maps.InfoWindow
  ): HTMLElement {
    const wrapperDiv = document.createElement('div');
    wrapperDiv.classList.add('location-card--wrapper');

    const button = document.createElement('button');
    button.setAttribute('tabindex', '0');
    button.classList.add('location-card--close');
    button.addEventListener('click', () =>
      this.infoWindowCloseClickHandler(infoWindow)
    );

    this.processAddress(location);
    this.processHours(location);
    this.generateDirectionUrl(location);

    const imageMarkup = () => {
      if (
        location.location_image !== '' &&
        location.location_image !== undefined
      ) {
        if (
          location.location_url !== undefined &&
          location.location_url !== ''
        ) {
          return `<div class="location-card--image"><a href="${location.location_url}"><img src="${location.location_image}"></img></a></div>`;
        } else {
          return `<div class="location-card--image"><img src="${location.location_image}"></img></div>`;
        }
      } else {
        return '';
      }
    };
    const line1Markup = () => {
      if (
        location.location_address_line_1 !== '' &&
        location.location_address_line_1 !== undefined
      ) {
        return `<div class="location-card--first-line">${location.location_address_line_1}</div>`;
      } else {
        return '';
      }
    };
    const line2Markup = () => {
      if (
        location.location_address_line_2 !== '' &&
        location.location_address_line_2 !== undefined
      ) {
        return `<div class="location-card--second-line">${location.location_address_line_2}</div>`;
      } else {
        return '';
      }
    };
    const cityStateZipMarkup = () => {
      if (
        location.location_city_state_zip !== '' &&
        location.location_city_state_zip !== undefined
      ) {
        return `<div class="location-card--city-state-zip">${location.location_city_state_zip}</div>`;
      } else {
        return '';
      }
    };
    const getDirectionsMarkup = () => {
      if (
        location.directions_url &&
        location.directions_url !== undefined &&
        location.directions_url !== ''
      ) {
        return `<a class="location-card--directions" href=${location.directions_url}>Get directions</a>`;
      } else {
        return '';
      }
    };
    const titleMarkup = () => {
      if (location.location_url !== undefined && location.location_url !== '') {
        return `<div class="location-card--title"><a href=${location.location_url}>${location.location_name}</a></div>`;
      } else if (
        location.location_name !== undefined &&
        location.location_name !== ''
      ) {
        return `<div class="location-card--title">${location.location_name}</div>`;
      } else {
        return '';
      }
    };
    const distanceMarkup = () => {
      if (
        location.distance &&
        location.distance !== undefined &&
        location.distance !== ''
      ) {
        return `<span class="location-card--drive-distance">${location.distance}</span>`;
      } else {
        return '';
      }
    };

    const driveMarkup = () => {
      if (
        location.drive_time &&
        location.drive_time !== undefined &&
        location.drive_time !== ''
      ) {
        return `<span class="location-card--drive-time">${location.drive_time} ETA</span>`;
      } else {
        return '';
      }
    };
    const phoneMarkup = () => {
      if (
        location.phone &&
        location.phone !== undefined &&
        location.phone !== ''
      ) {
        return `<a class="location-card--phone" href=tel:${formatPhoneNumberForHref(location.phone)}>${formatPhoneNumberForDisplay(location.phone)}</a>`;
      } else {
        return '';
      }
    };
    const faxMarkup = () => {
      if (location.fax && location.fax !== undefined && location.fax !== '') {
        return `<span class="location-card--fax"><span class="visually-hidden">Fax:</span>${formatPhoneNumberForDisplay(location.fax)}</span>`;
      } else {
        return '';
      }
    };

    const hoursMarkup = () => {
      if (location.hours_markup) {
        return `<div class="location-card--hours">
            <div class="location-card--hour-label">Hours</div>
            <ul class="location-card--hours-list">
              ${location.hours_markup}
            </ul>
          </div>`;
      } else {
        return '';
      }
    };

    const contactMarkup = `<div class="location-card--contact">
        ${hoursMarkup()}
      <div class="location-card--contact-info">
        ${phoneMarkup()}${faxMarkup()}
      </div>
    </div>`;

    const addressMarkup = `<div class="location-card--address"><div>${line1Markup()} ${line2Markup()} ${cityStateZipMarkup()}</div> ${getDirectionsMarkup()}</div>`;

    // Check if any contact information exists
    const hasContactInfo =
      (location.hours_markup && location.hours_markup !== '') ||
      (location.phone &&
        location.phone !== '' &&
        location.phone !== undefined) ||
      (location.fax && location.fax !== '' && location.fax !== undefined);

    const cardMarkup = `<div class="location-card">
        <div class="location-card--top-rail">
          ${imageMarkup()} ${titleMarkup()}
        </div>
        <div class="location-card--main-content ${!hasContactInfo ? 'no-contact' : ''}">
          <div class="location-card--info">
            ${addressMarkup}
            ${driveMarkup() !== '' ? `<div class="location-card--direction-info">${distanceMarkup()}${driveMarkup()}</div>` : ''}
          </div>
          ${contactMarkup}
        </div>`;
    wrapperDiv.innerHTML = cardMarkup;

    wrapperDiv.querySelector('.location-card--top-rail')?.appendChild(button);

    return wrapperDiv;
  }

  /**
   * Takes Drupal generated address string and creates the map directions url.
   */
  generateDirectionUrl(location: Location) {
    if (location.location_address && location.location_address !== '') {
      const urlPrefix = 'https://www.google.com/maps/dir/?api=1&destination=';
      const cleaned = location.location_address
        .replaceAll(' ', '%20')
        .replaceAll('\n', '');
      location.directions_url = `${urlPrefix}${cleaned}`;
      return location;
    } else {
      return;
    }
  }

  async fetchLocationData(url: string) {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw new Error('no data');
    }
  }

  async setLocationData(url?: string) {
    // allow for test data usage
    if (!url || url === 'false' || url === '') {
      if (locationTestData) {
        // Use type assertion to ensure test data matches Location[] type
        this.locationsData = locationTestData as unknown as Location[];
        this.initializeMap();
      }
    } else {
      await this.fetchLocationData(url)
        .then(fetchResponse => {
          this.locationsData = fetchResponse;
          this.initializeMap();
        })
        .catch(err => console.log(err));
    }
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (window.google?.maps?.places?.AutocompleteService) return;

    if (!(window as any)._googleMapsPromise) {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_DEVELOPMENT_GOOGLE_MAP_API_KEY,
        version: 'weekly',
        libraries: ['places'],
      });
      (window as any)._googleMapsPromise = loader.load();
    }

    await (window as any)._googleMapsPromise;
  }

  render(): TemplateResult {
    return html`<div id="map" class="size-${this.size}"></div>`;
  }
}
